import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiChevronDown, FiCopy, FiFilter, FiRefreshCcw } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button } from '~/components/shadcn/button-simple';
import { Card, CardContent } from '~/components/shadcn/card-simple';
import { Label } from '~/components/shadcn/label-simple';
import { Select } from '~/components/shadcn/select-simple';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/shadcn/table-simple';
import api from '~/services/api';
import copyTextToClipboard from '~/utils/copyTextToClipboard';

import { InventoryByUserResponse, InventoryFiltersForm, InventoryStatus, STATUS_OPTIONS, UserSummary } from './types';

function formatDate(value: string) {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function normalizeStatuses(value: unknown): InventoryStatus[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean) as InventoryStatus[];
  }

  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return value
      .split(',')
      .map((status) => status.trim())
      .filter(Boolean) as InventoryStatus[];
  }

  return [];
}

function toFriendlyStatus(status: string) {
  const matched = STATUS_OPTIONS.find((option) => option.value === status);
  return matched?.label ?? status;
}

function getStatusBadgeClass(status: string) {
  const base = 'inline-flex rounded-full border px-2 py-0.5 text-xs font-medium';

  switch (status) {
    case 'InStock':
      return `${base} border-emerald-200 bg-emerald-50 text-emerald-700`;
    case 'Allocated':
      return `${base} border-blue-200 bg-blue-50 text-blue-700`;
    case 'AwaitingShipment':
      return `${base} border-amber-200 bg-amber-50 text-amber-700`;
    case 'InUse':
      return `${base} border-sky-200 bg-sky-50 text-sky-700`;
    case 'ToBeInspected':
    case 'ToBeInspected-RMA':
      return `${base} border-orange-200 bg-orange-50 text-orange-700`;
    case 'InternalAsset':
      return `${base} border-indigo-200 bg-indigo-50 text-indigo-700`;
    case 'Failed':
      return `${base} border-red-200 bg-red-50 text-red-700`;
    default:
      return `${base} border-border bg-muted/30 text-foreground`;
  }
}

export function LocationInventoryByUser() {
  const profile = useSelector((state: any) => state.user.profile);
  const [users, setUsers] = useState<UserSummary[]>([]);
  const [inventory, setInventory] = useState<InventoryByUserResponse['inventory']>([]);
  const [totalAllItems, setTotalAllItems] = useState(0);
  const [totalFilteredItems, setTotalFilteredItems] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showStatusFilters, setShowStatusFilters] = useState(false);

  const statusFilterRef = useRef<HTMLDivElement | null>(null);
  const initializedFromProfileRef = useRef(false);

  const { register, watch, setValue, reset } = useForm<InventoryFiltersForm>({
    defaultValues: {
      userId: '',
      statuses: [],
    },
  });

  const selectedUserId = watch('userId');
  const watchedStatuses = watch('statuses');
  const selectedStatuses = useMemo(() => normalizeStatuses(watchedStatuses), [watchedStatuses]);

  const selectedUser = useMemo(() => users.find((user) => String(user.id) === selectedUserId) || null, [selectedUserId, users]);

  const selectedStatusesSet = useMemo(() => new Set(selectedStatuses), [selectedStatuses]);

  const hasStatusFilters = selectedStatuses.length > 0;

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setErrorMessage('');

    try {
      const response = await api.get<UserSummary[]>('/users');
      const fetchedUsers = response.data || [];

      setUsers(fetchedUsers);

      const profileId = profile?.id ? String(profile.id) : '';

      setValue('userId', profileId);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load users.';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoadingUsers(false);
    }
  }, [profile.id, setValue]);

  const fetchInventoryByUser = useCallback(async (userId: string, statuses: InventoryStatus[]) => {
    if (!userId) {
      setInventory([]);
      setTotalAllItems(0);
      setTotalFilteredItems(0);
      return;
    }

    setLoadingInventory(true);
    setErrorMessage('');

    const normalizedStatuses = normalizeStatuses(statuses);

    try {
      const [allItemsResponse, filteredResponse] = await Promise.all([
        api.get<InventoryByUserResponse>(`/locations/getInventoryByUser/${userId}`),
        api.get<InventoryByUserResponse>(`/locations/getInventoryByUser/${userId}`, {
          params: normalizedStatuses.length > 0 ? { status: normalizedStatuses.join(',') } : undefined,
        }),
      ]);

      const allQuantity = Number(allItemsResponse.data?.quantity ?? 0);
      const filteredQuantity = Number(filteredResponse.data?.quantity ?? 0);

      setTotalAllItems(allQuantity);
      setTotalFilteredItems(filteredQuantity);
      setInventory(filteredResponse.data?.inventory ?? []);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to load inventory for selected user.';
      setErrorMessage(message);
      toast.error(message);
      setInventory([]);
      setTotalAllItems(0);
      setTotalFilteredItems(0);
    } finally {
      setLoadingInventory(false);
    }
  }, []);

  const toggleStatus = useCallback(
    (status: InventoryStatus) => {
      const currentStatuses = normalizeStatuses(watch('statuses'));
      const nextStatuses = currentStatuses.includes(status) ? currentStatuses.filter((item) => item !== status) : [...currentStatuses, status];
      setValue('statuses', nextStatuses);
    },
    [setValue, watch]
  );

  const clearFilters = useCallback(() => {
    const currentUserId = watch('userId') || '';
    reset({ userId: currentUserId, statuses: [] });
  }, [reset, watch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (initializedFromProfileRef.current) return;
    if (!profile?.id) return;

    const profileId = String(profile.id);

    if (selectedUserId === profileId) {
      initializedFromProfileRef.current = true;
      return;
    }

    setValue('userId', profileId);
    fetchInventoryByUser(profileId, []);
    initializedFromProfileRef.current = true;
  }, [fetchInventoryByUser, profile?.id, selectedUserId, setValue]);

  useEffect(() => {
    if (!selectedUserId) return;
    fetchInventoryByUser(selectedUserId, selectedStatuses);
  }, [fetchInventoryByUser, selectedStatuses, selectedUserId]);

  useEffect(() => {
    if (!showStatusFilters) return undefined;

    const handleOutsideClick = (event: MouseEvent) => {
      if (!statusFilterRef.current) return;
      if (statusFilterRef.current.contains(event.target as Node)) return;
      setShowStatusFilters(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showStatusFilters]);

  const handleCopySerial = useCallback(async (serialNumber: string) => {
    const copied = await copyTextToClipboard(serialNumber);
    if (copied) {
      toast.success('Serial number copied');
      return;
    }
    toast.error('Unable to copy serial number');
  }, []);

  return (
    <div className="min-h-screen bg-white text-foreground lg:pl-64">
      <main className="space-y-5 px-4 pb-8 pt-20 lg:px-2 lg:pb-10">
        <section className="rounded-xl border border-border/80 bg-background/95 p-4 shadow-sm backdrop-blur-sm lg:p-5">
          <div className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2">
                <Link to="/location" className="inline-flex">
                  <Button variant="outline" className="h-8 w-fit gap-2 border-border/70 px-3 text-xs" type="button">
                    Back
                  </Button>
                </Link>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-xl font-semibold tracking-tight lg:text-2xl">Inventory By {selectedUser?.name || 'Selected User'}</h1>
                  <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs font-medium text-foreground">
                    Total: {totalAllItems}
                  </span>
                  <span className="inline-flex items-center rounded-full border border-border/70 bg-muted/40 px-3 py-1 text-xs font-medium text-foreground">
                    Filtered: {totalFilteredItems}
                  </span>
                </div>
              </div>

              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border/70 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
                <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
                {selectedUser ? `Selected user: ${selectedUser.name}` : 'No user selected'}
              </div>
            </div>

            <div className="grid gap-2 sm:grid-cols-12">
              <div className="space-y-1 sm:col-span-5">
                <input type="hidden" name="userId" ref={register({ required: true })} />
                <Label htmlFor="user-filter">Active User</Label>
                <Select
                  id="user-filter"
                  value={selectedUserId || ''}
                  onChange={(event) => setValue('userId', event.target.value)}
                  disabled={loadingUsers || users.length === 0}
                  className="h-9 border-border/70 bg-background"
                >
                  <option value="">Select a user</option>
                  {users.map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div ref={statusFilterRef} className="relative space-y-1 sm:col-span-4">
                <input type="hidden" name="statuses" ref={register()} />
                <Label htmlFor="status-filter-trigger">Status (Optional)</Label>
                <Button
                  id="status-filter-trigger"
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowStatusFilters((current) => !current)}
                  className="h-9 w-full justify-between border-border/70 bg-background px-2"
                >
                  <span className="inline-flex items-center gap-2">
                    <FiFilter className="h-4 w-4" />
                    {selectedStatuses.length} selected
                  </span>
                  <FiChevronDown className="h-4 w-4" />
                </Button>
                {showStatusFilters && (
                  <div className="absolute left-0 right-0 top-[calc(100%+0.25rem)] z-30 max-h-56 space-y-2 overflow-auto rounded-md border border-border/70 bg-background p-3 shadow-lg">
                    <button
                      type="button"
                      className="text-xs font-medium text-primary underline-offset-4 hover:underline"
                      onClick={() =>
                        setValue(
                          'statuses',
                          STATUS_OPTIONS.map((statusOption) => statusOption.value)
                        )
                      }
                    >
                      Select all
                    </button>
                    {STATUS_OPTIONS.map((statusOption) => (
                      <label key={statusOption.value} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedStatusesSet.has(statusOption.value)}
                          onChange={() => toggleStatus(statusOption.value)}
                          className="h-4 w-4"
                        />
                        <span>{statusOption.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-end gap-2 sm:col-span-3">
                <Button type="button" variant="outline" size="sm" onClick={clearFilters} disabled={!hasStatusFilters} className="h-9 flex-1">
                  Clear
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchInventoryByUser(selectedUserId, selectedStatuses)}
                  disabled={!selectedUserId || loadingInventory}
                  className="h-9 flex-1 gap-2"
                >
                  <FiRefreshCcw className="h-4 w-4" />
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </section>

        <Card className="border-border/80 shadow-sm">
          <CardContent className="p-0">
            {(loadingUsers || loadingInventory) && (
              <div className="space-y-3 p-4">
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
                <div className="h-10 animate-pulse rounded-md bg-muted" />
              </div>
            )}

            {!loadingUsers && !loadingInventory && errorMessage && (
              <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
                <p className="text-sm text-destructive">{errorMessage}</p>
                <Button type="button" variant="outline" onClick={() => fetchInventoryByUser(selectedUserId, selectedStatuses)} className="gap-2">
                  <FiRefreshCcw className="h-4 w-4" />
                  Retry
                </Button>
              </div>
            )}

            {!loadingUsers && !loadingInventory && !errorMessage && inventory.length === 0 && (
              <div className="p-8 text-center text-sm text-muted-foreground">No inventory found for the selected user and filters.</div>
            )}

            {!loadingUsers && !loadingInventory && !errorMessage && inventory.length > 0 && (
              <div className="max-h-[calc(100vh-24rem)] overflow-auto">
                <Table className="min-w-[1300px]">
                  <TableHeader className="sticky top-0 z-10 border-b bg-muted/50 backdrop-blur-sm">
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Serial Number</TableHead>
                      <TableHead className="whitespace-nowrap w-48">Status</TableHead>
                      <TableHead className="whitespace-nowrap">Grade</TableHead>
                      <TableHead className="whitespace-nowrap">Part Number</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="whitespace-nowrap">SKU</TableHead>
                      {/* <TableHead className="whitespace-nowrap">Previous Location</TableHead>
                      <TableHead className="whitespace-nowrap">Previous Location Date</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.map((row) => (
                      <TableRow key={row.id} className="border-b border-border/60 transition-colors hover:bg-muted/40">
                        <TableCell className="align-top">
                          <div className="flex items-center gap-2">
                            <a
                              href={`/inventoryProcessing/${row.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-primary underline-offset-4 hover:underline"
                            >
                              {row.serial_number}
                            </a>
                            <button
                              type="button"
                              onClick={() => handleCopySerial(row.serial_number)}
                              aria-label={`Copy serial number ${row.serial_number}`}
                              title="Copy serial number"
                              className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                              <FiCopy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <span className={getStatusBadgeClass(row.status)}>{toFriendlyStatus(row.status)}</span>
                        </TableCell>
                        <TableCell className="align-top">{row.grade || '-'}</TableCell>
                        <TableCell className="align-top font-medium">{row.item?.part_number || '-'}</TableCell>
                        <TableCell className="max-w-[460px] align-top">
                          <div className="line-clamp-2" title={row.item?.description || ''}>
                            {row.item?.description || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">{row.item?.sku || '-'}</TableCell>
                        {/* <TableCell className="align-top">{row.previous_location || '-'}</TableCell>
                        <TableCell className="align-top">{formatDate(row.previous_location_date)}</TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
